import { useCallback, useState } from 'react';
import AddLinkIcon from '@mui/icons-material/AddLink';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DataGridPremium as DataGrid,
  GridColDef,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { Document_FieldsFragment } from 'queries';

import DocumentPreviewModal from 'pages/common/document/document-preview-modal';
import DocumentTagPill from 'pages/common/document/document-tag-pill';
import SignatureTypePill from 'pages/common/document/signature-type-pill';
import DocumentTypeImage from 'pages/vault-page/components/document-type-image';

import FloatingMenu from './floating-menu';
import MoreMenu from './more-menu';
import ResolveDocumentForSignatureButton from './resolve-document-for-signature-button';

const useStyles = makeStyles(() => ({
  container: {
    fontWeight: 700,
    fontSize: 16,
    my: '24px',
    position: 'relative',
    marginBottom: '24px',
  },
  emptyContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    marginTop: '8px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    marginTop: '24px',
  },
  subtitle: {
    fontSize: '14px',
    marginTop: '12px',
  },
  dataGrid: {
    border: 0,
    borderRadius: '8px',
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-columnHeadersInner': {
      backgroundColor: '#F9F9FB',
      borderRadius: '8px',
      borderBottom: 'none',
    },
    '& .MuiDataGrid-row': {
      borderBottom: '1px solid #ECEEF1',
      borderTop: 'none',
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      borderTop: 'none',
    },
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: 'none',
      borderTop: 'none',
    },
  },
  documentTitle: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  documentSubtitle: {
    fontSize: '14px',
    marginRight: '8px',
  },
  dateUploadedHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  dateUploadedHeaderIcon: {
    marginRight: '8px',
  },
  dateUploadedValue: {
    fontSize: '14px',
    color: '#9DA8B4',
  },
  checkIcon: {
    color: 'green',
  },
  pointer: {
    cursor: 'pointer',
  },
}));

export enum DocumentTab {
  Uploads = 'uploads',
  GeneratedDocuments = 'generated-documents',
  SearchResults = 'search-results',
  DocumentsForSignature = 'documents-for-signature',
}

type Props = {
  documents: Document_FieldsFragment[];
  onRefreshDocuments: () => void;
  tab: DocumentTab;
  setDocumentGenerationTemplateToShowInstructions?: Function;
};

const EstateDocumentsTable = ({
  documents,
  onRefreshDocuments,
  tab,
  setDocumentGenerationTemplateToShowInstructions,
}: Props) => {
  const classes = useStyles();
  const [selectedPreviewDocument, setSelectedPreviewDocument] = useState<Document_FieldsFragment>();
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  const documentIds = documents.map((document) => document.id);

  const getTableColumns = useCallback(() => {
    const columns: GridColDef[] = [
      {
        flex: 2,
        field: 'name',
        renderHeader: () => <Typography>Name</Typography>,
        renderCell: (props) => {
          const file = props.row;

          return (
            <Box
              className={classes.documentTitle}
              onClick={() => setSelectedPreviewDocument(props.row)}
            >
              <DocumentTypeImage small fileName={file.name} isFolder={file.isFolder} />
              <Typography className={classes.documentSubtitle}>{props.value}</Typography>
            </Box>
          );
        },
      },
      {
        flex: 1,
        field: 'documentTag',
        renderHeader: () => (
          <Typography className={classes.dateUploadedHeader}>
            <FilterNoneIcon className={classes.dateUploadedHeaderIcon} fontSize="small" /> Document
            tag
          </Typography>
        ),
        renderCell: (props) => <DocumentTagPill documentTag={props.value} />,
      },
    ];

    let extraColumns: GridColDef[] = [];

    if (tab === DocumentTab.Uploads) {
      extraColumns = [
        {
          flex: 1,
          field: 'isSigned',
          renderHeader: () => (
            <Typography className={classes.dateUploadedHeader}>
              <AddLinkIcon className={classes.dateUploadedHeaderIcon} fontSize="small" /> Signed
            </Typography>
          ),
          renderCell: (props) => {
            return props.value && <CheckIcon className={classes.checkIcon} />;
          },
        },
        {
          flex: 1,
          field: 'createdAt',
          renderHeader: () => (
            <Typography className={classes.dateUploadedHeader}>
              <AddLinkIcon className={classes.dateUploadedHeaderIcon} fontSize="small" /> Date
              uploaded
            </Typography>
          ),
          renderCell: (props) => {
            return (
              <Typography className={classes.dateUploadedValue}>
                {new Date(props.value).toLocaleDateString()}
              </Typography>
            );
          },
        },
        {
          field: 'actions',
          renderHeader: () => '',
          renderCell: (props) => (
            <MoreMenu document={props.row} onRefreshDocuments={onRefreshDocuments} />
          ),
        },
      ];
    } else if (tab === DocumentTab.GeneratedDocuments) {
      extraColumns = [
        {
          flex: 1,
          field: 'createdAt',
          renderHeader: () => (
            <Typography className={classes.dateUploadedHeader}>
              <AddLinkIcon className={classes.dateUploadedHeaderIcon} fontSize="small" /> Date
              uploaded
            </Typography>
          ),
          renderCell: (props) => {
            return (
              <Typography className={classes.dateUploadedValue}>
                {new Date(props.value).toLocaleDateString()}
              </Typography>
            );
          },
        },
        {
          flex: 1,
          field: 'instructions',
          renderHeader: () => (
            <Typography className={classes.dateUploadedHeader}>
              <AddLinkIcon className={classes.dateUploadedHeaderIcon} fontSize="small" />{' '}
              Instructions
            </Typography>
          ),
          renderCell: (props) => {
            const file = props.row;
            if (!file.documentGenerationTemplate || !file.documentGenerationTemplate.instructions) {
              return '';
            }

            return (
              <>
                <OpenInFullIcon
                  className={clsx(classes.dateUploadedHeaderIcon, classes.pointer)}
                  fontSize="small"
                  onClick={() =>
                    setDocumentGenerationTemplateToShowInstructions!(
                      file.documentGenerationTemplate,
                    )
                  }
                />
                <Typography className={classes.dateUploadedValue}>
                  {file.documentGenerationTemplate.instructions}
                </Typography>
              </>
            );
          },
        },
        {
          field: 'actions',
          renderHeader: () => '',
          renderCell: (props) => (
            <MoreMenu document={props.row} onRefreshDocuments={onRefreshDocuments} />
          ),
        },
      ];
    } else if (tab === DocumentTab.SearchResults) {
      extraColumns = [
        {
          flex: 1,
          field: 'createdAt',
          renderHeader: () => (
            <Typography className={classes.dateUploadedHeader}>
              <AddLinkIcon className={classes.dateUploadedHeaderIcon} fontSize="small" /> Date
              uploaded
            </Typography>
          ),
          renderCell: (props) => {
            return (
              <Typography className={classes.dateUploadedValue}>
                {new Date(props.value).toLocaleDateString()}
              </Typography>
            );
          },
        },
        {
          field: 'actions',
          renderHeader: () => '',
          renderCell: (props) => (
            <MoreMenu document={props.row} onRefreshDocuments={onRefreshDocuments} />
          ),
        },
      ];
    } else if (tab === DocumentTab.DocumentsForSignature) {
      extraColumns = [
        {
          flex: 1,
          field: 'signatureType',
          renderHeader: () => (
            <Typography className={classes.dateUploadedHeader}>
              <img
                src="/signature_freehand.svg"
                alt="Success!"
                className={classes.dateUploadedHeaderIcon}
              />
              Signature type
            </Typography>
          ),
          renderCell: (props) => <SignatureTypePill signatureType={props.value} />,
        },
        {
          flex: 1,
          field: 'resolve',
          renderHeader: () => (
            <Typography className={classes.dateUploadedHeader}>
              <ErrorOutlineOutlinedIcon
                className={classes.dateUploadedHeaderIcon}
                fontSize="small"
              />{' '}
              Resolve
            </Typography>
          ),
          renderCell: (props) => (
            <ResolveDocumentForSignatureButton
              document={props.row}
              onRefreshDocuments={onRefreshDocuments}
            />
          ),
        },
        {
          field: 'actions',
          renderHeader: () => '',
          renderCell: (props) => (
            <MoreMenu
              document={props.row}
              onRefreshDocuments={onRefreshDocuments}
              isDocumentsForSignatureTab
            />
          ),
        },
      ];
    }

    return columns.concat(extraColumns);
  }, [onRefreshDocuments, tab, setDocumentGenerationTemplateToShowInstructions, classes]);

  if (documents.length === 0) {
    return (
      <Box className={classes.emptyContainer}>
        <img src="/estate-vault-empty-image.svg" alt="empty documents" />
        <Typography className={classes.title}>Nothing to show here yet</Typography>
        <Typography className={classes.subtitle}>
          All files {tab === DocumentTab.Uploads ? 'uploaded to the estate' : 'requested'} will show
          here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography className={classes.container}>
        {tab === DocumentTab.Uploads ? 'Uploaded Documents' : 'Documents for Signature'}
      </Typography>
      <DataGrid
        className={classes.dataGrid}
        columns={getTableColumns()}
        rows={documents}
        checkboxSelection
        disableColumnReorder
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooter
        columnHeaderHeight={45}
        autoHeight={true}
        onRowSelectionModelChange={setRowSelectionModel}
        rowSelectionModel={rowSelectionModel}
      />
      {rowSelectionModel.length > 0 && (
        <FloatingMenu
          documentIds={documentIds}
          rowSelectionModel={rowSelectionModel}
          setRowSelectionModel={setRowSelectionModel}
          onRefreshDocuments={onRefreshDocuments}
          tab={tab}
        />
      )}
      {selectedPreviewDocument && (
        <DocumentPreviewModal
          setShowModal={() => setSelectedPreviewDocument(undefined)}
          showModal={true}
          document={selectedPreviewDocument}
        />
      )}
    </Box>
  );
};

export default EstateDocumentsTable;
